import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { ReactComponent as QuestionIcon } from 'i/icons/question.svg'

import { YoutubeVideoHandler } from 'lib'
import { generatePaymentIntentId } from 'utils/generate-payment-intent-id'

const videoHandler = new YoutubeVideoHandler()

const ProfileTabs = ({ user, pro, events, onTabChange, windowWidth }) => {
  const history = useHistory()

  const dateOptions = {
    weekday: 'short',
    year: '2-digit',
    month: 'short',
    day: 'numeric',
  }

  const [userVideos, setUserVideos] = useState(null)
  const [tabsLinks, setTabsLinks] = useState([])
  const [activeTab, setActiveTab] = useState(0) // Initialize to 0, will be updated in useEffect

  // Update tabsLinks and userVideos after data is set
  useEffect(() => {
    let videoUrls = []

    if (pro?.video_urls) {
      videoUrls = JSON.parse(pro?.video_urls)
    } else if (user?.video_urls) {
      videoUrls = JSON.parse(user?.video_urls)
    }

    setUserVideos(videoUrls)

    // Set tabsLinks based on windowWidth, events, and userVideos
    const newTabsLinks = [
      ...(windowWidth < 1024 ? ['Sessions'] : []),
      ...(events?.length ? ['Classes'] : []),
      ...(videoUrls?.length ? ['Videos'] : []),
    ]

    setTabsLinks(newTabsLinks)

    // Set activeTab to 0 to select the first tab
    setActiveTab(0)
  }, [pro, user, events, windowWidth])

  const tabHandler = (index) => {
    setActiveTab(index)
    onTabChange(index)
  }

  useEffect(() => {
    if (tabsLinks.length > 0) {
      onTabChange(activeTab)
    }
  }, [activeTab, tabsLinks, onTabChange])

  const handleEventClick = (e, event) => {
    e.preventDefault()

    const eventEntryFee = parseInt(event?.entry_fee)

    const eventData = {
      event: {
        id: event?.id,
        entry_fee: event?.entry_fee,
        scheduled_at: event?.scheduled_at,
        category: event?.category,
        description: event?.description,
        max_participants: event?.max_participants,
      },
      pro,
    }

    if (eventEntryFee !== 0) {
      if (user) {
        history.push('/user/payments', eventData)
      } else {
        history.push('/user/confirm', eventData)
      }
    }

    if (eventEntryFee === 0) {
      if (user) {
        const paymentIntentId = generatePaymentIntentId()
        const callbackRoute = `/user/callback/?payment_intent=${paymentIntentId}`
        window.localStorage.setItem('isEvent', 'true')
        window.localStorage.setItem('info', JSON.stringify(eventData))
        history.push(callbackRoute, eventData)
      } else {
        history.push('/user/confirm', eventData)
      }
    }
  }

  // Dynamically generate content based on tabsLinks
  const getTabContent = (tabName) => {
    if (tabName === 'Sessions') {
      return (
        <div className="profile_sessions">
          <div className="profile_sessions__list"></div>
        </div>
      );
    }
    if (tabName === 'Videos' && userVideos?.length) {
      return (
        <div className="profile_media">
          <div className="profile_media__list">
            {userVideos.map(({ videoId, title, duration }, index) => (
              <div className="profile_media__item" key={videoId + index}>
                <div className="profile_media__iframe_item">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}?showinfo=0`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="profile_media__title">{title}</div>
                <div className="profile_media__duration">
                  {videoHandler.convertDuration(duration)}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (tabName === 'Classes' && events?.length > 0) {
      return (
        <div className="profile_events">
          {events.map((event) => (
            <div className="profile_events__item" key={event.id} onClick={(e) => handleEventClick(e, event)}>
              <div className="profile_events__title">{event?.title}</div>
              <div className="profile_events__date">
                {new Date(event?.scheduled_at).toLocaleDateString('en-US', dateOptions)}
              </div>
              <button className="profile_events__info_icon">
                <QuestionIcon />
              </button>
              <div className="profile_events__info">
                <div className="profile_events__info_list">
                  {parseInt(event?.entry_fee) !== 0 && (
                    <div className="profile_events__info_item">
                      {`${parseInt(event?.entry_fee).toFixed(0).toString()} entry`}
                    </div>
                  )}
                  {parseInt(event?.max_participants) !== 0 && (
                    <div className="profile_events__info_item">{`${event?.max_participants} Max Attendance`}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {tabsLinks.length > 0 ? (
        <div className="profile_tabs">
          <div className="profile_tabs__head">
            {tabsLinks.map((item, index) => (
              <div className="profile_tabs__item" key={index}>
                <a
                  href={`/tab-${index}`}
                  className={`profile_tabs__link ${index === activeTab ? 'profile_tabs__link--active' : ''}`}
                  data-tab={index}
                  onClick={(e) => {
                    e.preventDefault()
                    tabHandler(index)
                  }}
                >
                  {item}
                </a>
              </div>
            ))}
          </div>
          <div className="profile_tabs__content">
            {tabsLinks.map((tabName, index) => (
              <div
                className={`profile_tabs__content_item ${index === activeTab ? 'profile_tabs__content_item--active' : ''}`}
                key={index}
              >
                {getTabContent(tabName)}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  )
}

export default ProfileTabs
